'use strict'

function noop() {

}

function offset(element, from) {
  return element.getBoundingClientRect()[from] + (window.scrollY || window.pageYOffset)
}

function rect(element, key) {
  return element.getBoundingClientRect()[key]
}

/**
 * @type {object} defaults
 */
const defaults = {
  top: 0, /* Where to stick from top */
  bottom: 0, /* Extra added to stopper selector */
  breakpoint: 0, /* Where to stop or start */
  resize: false, /* Handle resize with js, suited for sidebars and responsive */
  classOnly: true, /* Sticky only with css class with position fixed */
  elevation: false, /* If sticky element is bigger than screen, turn it off */
  fixClass: 'is-fixed', /* Default sticky class */
  selector: '.js-sticky-element', /* The element selector to stick */
  placeholder: true, /* If false, no placeholder is created */
  placeholderClass: 'sticky-element-placeholder', /* Placeholder css class */
  onBreakpointAbove: noop, /* function to be called when above breakpoint */
  onBreakpointBelow: noop /* function to be called when bellow breakpoint */
}

/**
 * @class StickyElement
 * @constructor
 * @param {object} options - plugin options
 * @param {HTMLElement} element - html element
 */
function StickyElement(options, element) {
  this.options = Object.assign({}, defaults, options)
  this.element = element || document.querySelector(this.options.selector)
  this.stopper = document.querySelector(this.options.stopSelector)
  this.fixed = false
  this.ticking = false
  this.pinned = false

  if (this.options.placeholder) {
    this.placeholder = document.createElement('div')
    this.placeholder.className = this.options.placeholderClass
  }

  this.scrollTop = window.scrollY || window.pageYOffset
  this.initial = this.bounds()

  this.turnOn = this.turnOn.bind(this)
  this.turnOff = this.turnOff.bind(this)

  this.boundOnScroll = this.onScroll.bind(this)
  this.boundOnResize = this.onResize.bind(this)
  this.boundSwitchHandler = this.switchHandler.bind(this)

  this.boundBreakpointHandler = this.breakpointHandler.bind(this)
  this.onBreakpointAbove = this.options.onBreakpointAbove.bind(this)
  this.onBreakpointBelow = this.options.onBreakpointBelow.bind(this)

  this.turnOn()
}

StickyElement.prototype.bounds = function() {
  return {
    top: offset(this.element, 'top'),
    bottom: offset(this.element, 'bottom'),
    width: rect(this.element, 'width'),
    height: rect(this.element, 'height'),
    position: this.element.style.position
  }
}


StickyElement.prototype.css = function(def) {
  Object.keys(def).forEach(function(key) {
    this.element.style[key] = def[key]
  }, this)
}


StickyElement.prototype.addPlaceholder = function() {
  if (this.options.placeholder) {
    this.placeholder.style.height = this.initial.height + 'px'
    this.element.parentElement.insertBefore(this.placeholder, this.element)
  }
}


StickyElement.prototype.fix = function() {
  this.addPlaceholder()

  if (!this.options.classOnly) {
    this.css({
      position: 'fixed',
      top: this.options.top + 'px',
      width: this.initial.width + 'px'
    })
  }

  this.element.classList.add(this.options.fixClass)
  this.fixed = true
}


StickyElement.prototype.unfix = function() {
  if (document.body.contains(this.placeholder)) {
    this.placeholder.parentElement.removeChild(this.placeholder)
  }

  if (!this.options.classOnly) {
    this.css({
      position: this.initial.position,
      top: 'auto',
      width: 'auto'
    })
  }

  this.element.classList.remove(this.options.fixClass)
  this.fixed = false
}


StickyElement.prototype.pin = function() {
  this.css({
    transform: `translate3d(0, ${this.diff}px, 0)`
  })
  this.pinned = true
}


StickyElement.prototype.unpin = function() {
  this.css({
    transform: 'translate3d(0, 0, 0)'
  })
  this.pinned = false
}


StickyElement.prototype.requestTick = function(method) {
  if (!this.ticking) {
    window.requestAnimationFrame(method.bind(this))
  }
  this.ticking = true
}


StickyElement.prototype.update = function() {
  if (this.scrollTop + this.options.top >= this.initial.top) {
    if (!this.fixed) {
      this.fix()
    }
  }
  else {
    if (this.fixed) {
      this.unfix()
    }
  }

  if (this.stopper) {
    this.diff <= 0 ? this.pin() : this.unpin()
  }

  this.ticking = false
}


StickyElement.prototype.calcDiff = function() {
  if (this.stopper) {
    let { top, bottom } = this.options
    this.diff = rect(this.stopper, 'top') - rect(this.element, 'height') - top - bottom
  }
}


StickyElement.prototype.reset = function() {
  this.unfix()
  this.unpin()
  this.initial = this.bounds()
}


StickyElement.prototype.onScroll = function() {
  this.calcDiff()
  this.scrollTop = window.scrollY || window.pageYOffset
  this.requestTick(this.update)
}


StickyElement.prototype.onResize = function() {
  this.reset()
  this.calcDiff()
  this.requestTick(this.update)
}


/*
 * Turn Sticky Behaviour on/off according to breakpoint and elevation options
 */
StickyElement.prototype.switchHandler = function() {
  let { top, bottom, breakpoint, elevation } = this.options
  let stickyHeight = Math.floor(this.bounds()['height']) + top + bottom
  let clientWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
  let clientHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0)

  if (breakpoint && elevation) {
    if ((clientWidth >= breakpoint) && (stickyHeight <= clientHeight)) {
      setTimeout(this.turnOn)
      return
    }


    if ((clientWidth <= breakpoint) && (stickyHeight >= clientHeight)) {
      setTimeout(this.turnOff)
      return
    }

    if ((clientWidth <= breakpoint) && (stickyHeight <= clientHeight)) {
      setTimeout(this.turnOff)
      return
    }

    if ((clientWidth >= breakpoint) && (stickyHeight >= clientHeight)) {
      setTimeout(this.turnOff)
      return
    }

    return
  }

  if (breakpoint) {
    setTimeout(clientWidth <= breakpoint ? this.turnOff : this.turnOn)
    return
  }

  if (elevation) {
    setTimeout(stickyHeight >= clientHeight ? this.turnOff : this.turnOn)
    return
  }
}


/*
 * Handle client attached handlers below/above breakpoint
 */
StickyElement.prototype.breakpointHandler = function() {
  let { breakpoint } = this.options
  let clientWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)

  if (!breakpoint) return

  if (clientWidth >= breakpoint) {
    if (!this._breakpointAboveHandlerExecuted) {
      this.onBreakpointAbove()
      this._breakpointAboveHandlerExecuted = true
      this._breakpointBelowHandlerExecuted = false
    }
  }
  else {
    if (!this._breakpointBelowHandlerExecuted) {
      this.onBreakpointBelow()
      this._breakpointBelowHandlerExecuted = true
      this._breakpointAboveHandlerExecuted = false
    }
  }
}


/*
 * Turn ON sticky behaviour
 */
StickyElement.prototype.turnOn = function() {
  if (this._switchedOn) return

  let { resize, breakpoint, elevation, onBreakpointBelow, onBreakpointAbove } = this.options

  this.boundOnScroll()
  this.boundOnResize()
  this.boundSwitchHandler()
  this.boundBreakpointHandler()

  window.addEventListener('scroll', this.boundOnScroll, false)
  if (resize) window.addEventListener('resize', this.boundOnResize, false)
  if (breakpoint || elevation) window.addEventListener('resize', this.boundSwitchHandler, false)
  if (breakpoint && (onBreakpointBelow !== noop || onBreakpointAbove !== noop)) window.addEventListener('resize', this.boundBreakpointHandler, false)

  this.reset()
  this._switchedOn = true
}


/*
 * Turn OFF sticky behaviour
 */
StickyElement.prototype.turnOff = function() {
  if (!this._switchedOn) return

  window.removeEventListener('scroll', this.boundOnScroll, false)
  window.removeEventListener('resize', this.boundOnResize, false)
  window.removeEventListener('resize', this.boundBreakpointHandler, false)

  this.reset()
  this._switchedOn = false
}

/*
 * Factory
 */
StickyElement.create = function(options) {
  options = options || {}
  return new StickyElement(options)
}

// Attach to jquery if presented
if (window.jQuery) {
  $.fn.StickyElement = function(options) {
    return this.each(function() {
      if (!$.data(this, 'StickyElement')) {
        $.data(this, 'StickyElement', new StickyElement(options, this))
      }
    })
  }

  // lowercase, so we can initialize via data attr parser
  $.fn.stickyelement = $.fn.StickyElement;
}

// Attach to window
window.StickyElement = StickyElement

// DEFAULT EXPORT
export default StickyElement

